import { useState } from "react";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import { AppDialog } from "../../components/AppDialog";
import { AppDialogHeader } from "../../components/AppDialogHeader";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { apiFetch } from "../../../api";
import { requestLock } from "../../../lockBus";
import { showToast } from "../../../toast";

// Contextual single-field edit, not a page of its own - same POST
// /api/profile the old always-visible form used, just saved on its own
// instead of behind a page-wide Save button. Fullscreen AppDialog like
// every other dialog in the app (see AppDialog.tsx's "no floating modal
// card surfaces" comment) rather than a smaller drawer, for consistency.
export function EditNameSheet({ currentName, onClose }: { currentName: string; onClose: () => void }) {
  const [name, setName] = useState(currentName);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleSave(): Promise<void> {
    const trimmed = name.trim();
    if (trimmed.length > 100) {
      setError("Name is too long.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const res = await apiFetch("/api/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fullName: trimmed }),
      });
      if (res.status === 401) {
        requestLock("unauthorized");
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || "Failed to save your name.");
        return;
      }
      appState.currentUserFullName = trimmed;
      notifyStateChange();
      showToast("Name updated.", "success");
      onClose();
    } catch {
      setError("Server error. Try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppDialog open onClose={onClose} maxWidth="xs" fullWidth>
      <AppDialogHeader icon="bi-person" title="Edit name" onBack={onClose} disabled={saving} />
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "grid", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            This is the name your partner will see.
          </Typography>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            autoComplete="name"
            autoFocus
            fullWidth
            size="small"
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
          {error && (
            <Alert severity="error" sx={{ py: 0.5 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </AppDialog>
  );
}
